/**
 * Keyboard lift (ux-flow.md §0 rule 7; the prototype's liftField/fitLifted).
 * A focused input rises just far enough for the field and whatever it opens
 * (suggestions, calendar) to sit above the keyboard. If it already fits, nothing
 * moves, so the question stays in view. It never tucks the field under the chrome.
 *
 * Everything inside the field marked `data-lift-extra` (a suggestion list, say) counts
 * as part of it; pass `extra` for something outside the field (the S2 calendar).
 */
const KB_SHARE = 0.4; // assumed keyboard height on touch until the real one shows up
const KB_CLASS = 'kb';

let lifted: { field: HTMLElement; extra?: HTMLElement | null } | null = null;

function keyboardTop(): number {
  const vv = window.visualViewport;
  if (vv && vv.height < window.innerHeight - 80) return vv.offsetTop + vv.height; // real keyboard is up
  if (window.matchMedia?.('(pointer: coarse)').matches) return window.innerHeight * (1 - KB_SHARE);
  const dock = document.querySelector<HTMLElement>('.dock');
  return window.innerHeight - (dock ? dock.offsetHeight : 0);
}

const visible = (el: HTMLElement | null | undefined): el is HTMLElement =>
  !!el && !el.hidden && !el.closest('[hidden]');

/** Scroll by the smallest amount that puts the lifted field (and its extras) above the keyboard. */
export function fitLifted(): void {
  if (!lifted || !document.body.classList.contains(KB_CLASS)) return;
  const { field, extra } = lifted;
  const chrome = document.querySelector<HTMLElement>('.chrome');
  const chromeH = chrome && !chrome.hidden ? chrome.offsetHeight : 0;
  const parts = [field, extra, ...field.querySelectorAll<HTMLElement>('[data-lift-extra]')].filter(visible);
  const bottom = Math.max(...parts.map((el) => el.getBoundingClientRect().bottom));
  const overflow = bottom + 8 - keyboardTop();
  const room = field.getBoundingClientRect().top - chromeH - 8; // never tuck the field under the chrome
  const dy = Math.min(overflow, room);
  if (dy > 1) window.scrollTo({ top: window.scrollY + dy, behavior: 'smooth' });
}

export function liftField(field: HTMLElement, extra?: HTMLElement | null): void {
  document.body.classList.add(KB_CLASS);
  lifted = { field, extra };
  requestAnimationFrame(fitLifted);
}

/** Drop the lift (after a pick, or on navigation). */
export function settle(): void {
  document.body.classList.remove(KB_CLASS);
  lifted = null;
}

/** Installed once by the shell. Returns the uninstaller. */
export function installKeyboardLift(): () => void {
  const vv = window.visualViewport;
  vv?.addEventListener('resize', fitLifted);
  let t: ReturnType<typeof setTimeout> | undefined;
  // Let the page settle back once nothing is focused.
  const onFocusOut = () => {
    clearTimeout(t);
    t = setTimeout(() => {
      const el = document.activeElement;
      if (!el || !['INPUT', 'TEXTAREA'].includes(el.tagName)) settle();
    }, 120);
  };
  document.addEventListener('focusout', onFocusOut);
  return () => {
    clearTimeout(t);
    vv?.removeEventListener('resize', fitLifted);
    document.removeEventListener('focusout', onFocusOut);
  };
}

/**
 * onFocus handler for an input: lifts its closest `.field` (or the input itself).
 * `<input onFocus={liftOnFocus} />`, or `onFocus={(e) => liftOnFocus(e, calendarRef.current)}`.
 */
export function liftOnFocus(e: { currentTarget: HTMLElement }, extra?: HTMLElement | null): void {
  const el = e.currentTarget;
  liftField(el.closest<HTMLElement>('.field') ?? el, extra);
}
